import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { api, type ApiResponse } from "@/api/client";
import { DndCSS, type DndTransform } from "@/lib/dndHelpers";
import DraggableList from "@/components/common/DraggableList";
import type { FAQ } from "@ckgo2/shared/types";
import { Plus, Pencil, Trash2, Loader2, GripVertical, ChevronUp, ChevronDown } from "lucide-react";

function SortableFaqItem({ faq, onDelete }: { faq: FAQ; onDelete: (id: string) => void }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: faq.id,
  });

  const style: React.CSSProperties = {
    transform: DndCSS.Transform.toString(transform as DndTransform | null),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const statusLabels: Record<string, string> = {
    draft: t("common.status.draft"),
    published: t("common.status.published"),
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center justify-between py-3">
      <div className="flex min-w-0 flex-1 items-center gap-3 pr-4">
        <button
          type="button"
          className="cursor-grab touch-none text-gray-400 hover:text-gray-600"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-gray-900 dark:text-white">{faq.question}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t("content.faqs.list.meta", {
              order: faq.sortOrder,
              status: statusLabels[faq.status] || faq.status,
            })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to={`/faqs/${faq.id}`}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
        >
          <Pencil className="h-4 w-4" />
        </Link>
        <button
          title={t("common.actions.delete")}
          onClick={() => {
            if (confirm(t("content.faqs.list.confirmDelete"))) onDelete(faq.id);
          }}
          className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function FaqsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["faqs"],
    queryFn: () => api.get<ApiResponse<FAQ[]>>("/api/admin/faqs"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/faqs/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["faqs"] }),
  });

  const reorderMutation = useMutation({
    mutationFn: (items: { id: string; sortOrder: number; topicSortOrder: number }[]) =>
      api.patch("/api/admin/faqs/reorder", { items }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["faqs"] }),
  });

  const faqs = data?.data || [];

  // Local state for optimistic reordering
  const [localFaqs, setLocalFaqs] = useState<FAQ[]>([]);

  useEffect(() => {
    setLocalFaqs(faqs);
  }, [faqs]);

  // Group by topic
  const grouped: Record<string, FAQ[]> = {};
  for (const faq of localFaqs) {
    if (!grouped[faq.topic]) grouped[faq.topic] = [];
    grouped[faq.topic].push(faq);
  }

  const topics = Object.entries(grouped).sort(
    (a, b) => (a[1][0]?.topicSortOrder || 0) - (b[1][0]?.topicSortOrder || 0),
  );

  const handleReorder = (topic: string, oldIndex: number, newIndex: number) => {
    const topicItems = grouped[topic].sort((a, b) => a.sortOrder - b.sortOrder);
    const reordered = arrayMove(topicItems, oldIndex, newIndex);

    // Update local state optimistically
    const updatedFaqs = localFaqs.map((faq) => {
      const idx = reordered.findIndex((r) => r.id === faq.id);
      if (idx !== -1) return { ...faq, sortOrder: idx };
      return faq;
    });
    setLocalFaqs(updatedFaqs);

    // Send reorder to server
    reorderMutation.mutate(
      reordered.map((item, idx) => ({
        id: item.id,
        sortOrder: idx,
        topicSortOrder: item.topicSortOrder,
      })),
    );
  };

  const moveTopic = (topicIndex: number, direction: -1 | 1) => {
    const targetIndex = topicIndex + direction;
    if (targetIndex < 0 || targetIndex >= topics.length) return;
    const newTopics = [...topics];
    [newTopics[topicIndex], newTopics[targetIndex]] = [
      newTopics[targetIndex],
      newTopics[topicIndex],
    ];

    const items: { id: string; sortOrder: number; topicSortOrder: number }[] = [];
    newTopics.forEach(([, topicItems], tIdx) => {
      for (const faq of topicItems) {
        items.push({ id: faq.id, sortOrder: faq.sortOrder, topicSortOrder: tIdx });
      }
    });

    const updatedFaqs = localFaqs.map((faq) => {
      const found = items.find((i) => i.id === faq.id);
      if (found) return { ...faq, topicSortOrder: found.topicSortOrder };
      return faq;
    });
    setLocalFaqs(updatedFaqs);
    reorderMutation.mutate(items);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-500 h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("content.faqs.list.title")}
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">{t("content.faqs.list.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          {reorderMutation.isPending && (
            <span className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("content.faqs.list.savingOrder")}
            </span>
          )}
          <Link to="/faqs/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            {t("content.faqs.list.addFaq")}
          </Link>
        </div>
      </div>

      {topics.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-12 text-center dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">{t("content.faqs.list.empty")}</p>
        </div>
      ) : (
        topics.map(([topic, items], topicIndex) => {
          const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
          return (
            <div key={topic} className="card">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{topic}</h2>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveTopic(topicIndex, -1)}
                    disabled={topicIndex === 0}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700"
                    title={t("content.faqs.list.moveTopicUp")}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveTopic(topicIndex, 1)}
                    disabled={topicIndex === topics.length - 1}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent dark:hover:bg-gray-700"
                    title={t("content.faqs.list.moveTopicDown")}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <DraggableList
                itemIds={sortedItems.map((f) => f.id)}
                onReorder={(oldIndex, newIndex) => handleReorder(topic, oldIndex, newIndex)}
              >
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sortedItems.map((faq) => (
                    <SortableFaqItem
                      key={faq.id}
                      faq={faq}
                      onDelete={(id) => deleteMutation.mutate(id)}
                    />
                  ))}
                </div>
              </DraggableList>
            </div>
          );
        })
      )}
    </div>
  );
}
